import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { IconButton, Tooltip } from '@mui/material';
import React from 'react';
import ProjectCostReportDialog from './payroll/dialogs/ProjectCostReportDialog';
import { computeProjectCostRows } from './payroll/projectCostFormula';

const fmtCur = (n) => new Intl.NumberFormat('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const TH = { fontSize: '0.75rem', fontWeight: 700, background: '#ffedd5', color: '#9a3412', padding: '6px 8px', textAlign: 'center', borderBottom: '1px solid #fed7aa', borderRight: '1px solid #fed7aa' };
const TD_TOTAL = { fontWeight: 700, background: '#ffedd5', color: '#9a3412', borderTop: '2px solid #f97316', borderRight: '1px solid #fdba74', padding: '6px 8px', textAlign: 'right' };
const TD_ROW = { padding: '5px 8px', borderBottom: '1px solid #fed7aa', borderRight: '1px solid #fed7aa', fontSize: '0.82rem' };
const IS = { padding: '2px 4px', fontSize: '0.78rem', textAlign: 'right', width: '100%', border: '1px solid #d1d5db', borderRadius: 4, outline: 'none', boxSizing: 'border-box' };

const DEFAULT_OT_MULT = 1.5;

// ── แยกแต่ละโครงการเป็น 2 แถว (ปกติ / ภายนอก) ──
// พนักงานที่ติ๊ก "ไม่จ่ายเงินตามโครงการ" (Employee.no_project_payment) — ต้นทุนยังนับในโครงการเหมือนเดิม แต่รายการเบิก
// ค่าแรงของคนกลุ่มนี้ถูกแยกเป็นอีกใบ (ผู้รับเงิน = บริษัท ซี.ดี. จำกัด ดู expenses.service.js laborEmployeeSplit) แถวนี้จึงแยกตาม
// MergedPayrollTableV2.js ติดป้าย external ไว้ที่ employees/employee_deductions/employee_payables (งวดเก่าไม่มี = ไม่แยก)
const sumBy = (arr, key) => Math.round((arr || []).reduce((s, x) => s + (Number(x?.[key]) || 0), 0) * 100) / 100;

function buildGroupRow(p, ext) {
  const pick = (arr) => (arr || []).filter((x) => !!x.external === ext);
  const employees = pick(p.employees);
  const employee_deductions = pick(p.employee_deductions);
  const employee_payables = pick(p.employee_payables);
  const total_salary = sumBy(employees, 'amount');
  const total_ot = sumBy(employees, 'ot_amount');
  const total_allowance = sumBy(employees, 'allowance');
  const total_field_allowance = sumBy(employees, 'field_allowance_amount');
  const total_sso = sumBy(employee_deductions, 'sso');
  const total_other = sumBy(employee_deductions, 'other');
  const total_crl = sumBy(employee_deductions, 'crl');
  const total_bonus = sumBy(employee_deductions, 'bonus');
  // total_late / amount ไม่มีรายคนในข้อมูลโครงการ — ให้อยู่แถวปกติทั้งก้อน
  const total_late = ext ? 0 : (p.total_late || 0);
  const amount = ext ? 0 : (p.amount || 0);
  return {
    ...p,
    external: ext,
    employees,
    employee_deductions,
    employee_payables,
    total_salary,
    total_ot,
    total_allowance,
    total_field_allowance,
    total_sso,
    total_other,
    total_crl,
    total_bonus,
    total_late,
    amount,
    total_payable: sumBy(employee_payables, 'amount'),
    total_spent: total_salary + total_ot + total_sso + total_late + amount + total_allowance + total_field_allowance + total_bonus,
  };
}

export function splitExternalRows(rows) {
  const out = [];
  (rows || []).forEach((p) => {
    const hasExternal = [...(p.employees || []), ...(p.employee_deductions || []), ...(p.employee_payables || [])]
      .some((x) => x.external);
    if (!hasExternal) {
      out.push(p);
      return;
    }
    const normal = buildGroupRow(p, false);
    // ทุกคนในโครงการนี้เป็นพนักงานภายนอกหมด → ไม่ต้องมีแถวปกติว่างๆ
    if (normal.employees.length + normal.employee_deductions.length + normal.employee_payables.length > 0) {
      out.push(normal);
    }
    out.push(buildGroupRow(p, true));
  });
  return out;
}

// สถานะของรายการเบิกค่าแรงที่สร้างอัตโนมัติ (Expenses.status) → ป้ายภาษาไทย
function laborStatusInfo(status) {
  if (['PENDING', 'PREAPPROVE', 'APPROVE_1', 'APPROVE_2'].includes(status)) return { label: 'รออนุมัติ', bg: '#fff3cd', color: '#92400e' };
  if (status === 'APPROVE') return { label: 'อนุมัติแล้ว', bg: '#dcfce7', color: '#15803d' };
  if (status === 'PREPARE') return { label: 'เตรียมจ่าย', bg: '#dbeafe', color: '#1d4ed8' };
  if (status === 'SUCCESS') return { label: 'จ่ายแล้ว', bg: '#f3e8ff', color: '#6b21a8' };
  if (String(status).startsWith('REJECT')) return { label: 'ปฏิเสธ', bg: '#fee2e2', color: '#dc2626' };
  if (status === 'HOLD') return { label: 'พัก', bg: '#fee2e2', color: '#dc2626' };
  if (status === 'CANCEL') return { label: 'ยกเลิก', bg: '#f3f4f6', color: '#6b7280' };
  return { label: status || '—', bg: '#f3f4f6', color: '#6b7280' };
}

// totalSsoAmount ไม่ได้ใช้ในไฟล์นี้แล้ว (total_sso คำนวณจาก employee_deductions ใน computeProjectCostRows เอง)
// ผู้เรียกที่ยังส่ง prop นี้มา (เช่น HrReport.js) ไม่ต้องแก้ — extra prop ที่ไม่ถูก destructure ไว้ก็แค่เฉยๆ
// คอลัมน์ "รายการเบิก" (ปุ่มสร้างรายการเบิกค่าแรง manual) ถูกเอาออกแล้ว — รายการเบิกค่าแรงสร้างเฉพาะตอน
// กดบันทึกเงินเดือน (submitLaborDraftsToPending) เท่านั้น
export default function TimestampProjectCost({
  timestamp, filteredEmployeeIds,
  // สถานะรายการเบิกค่าแรงที่สร้างอัตโนมัติ [{ project_id, employee_id|null, status, code, price }] —
  // ไม่ส่งมา (เช่น หน้าดูงวดที่บันทึกแล้ว) = ไม่แสดงคอลัมน์ "สถานะ"
  laborStatuses,
  // ส่งมา = โชว์ปุ่ม "ดูสูตรคำนวณค่าแรง" ที่มุมขวาของแถบตัวคูณ OT (เปิด dialog ที่ HrReportV2.js)
  onOpenFormulaExplain,
  // ส่งมา = โชว์ปุ่มสูตรในแถวโครงการ (กดแล้วส่งแถวนั้นกลับไป) — ดูว่ายอดของโครงการมาจากใครกี่บาท
  onOpenProjectFormula,
}) {
  const ovKey = (projId, empId) => `${projId}__${empId}`;
  const [overrides, setOverrides] = React.useState({});
  const [globalOtMult, setGlobalOtMult] = React.useState(DEFAULT_OT_MULT);
  const [, setExpandedProjects] = React.useState(new Set());

  const computeEmp = React.useCallback(
    (emp, projId) => {
      const key = ovKey(projId, emp.employee_id);
      const ov = overrides[key] || {};
      const origHours = Math.round((emp.rate || 0) * 8 * 100) / 100;
      const origRate = Math.round(((emp.salary_per_day || 0) / 8) * 100) / 100;
      const origOtHours = origRate > 0 ? Math.round(((emp.ot_amount || 0) / (origRate * DEFAULT_OT_MULT)) * 100) / 100 : 0;
      const hours = ov.hours !== undefined ? parseFloat(ov.hours) || 0 : origHours;
      const ratePerHour = ov.ratePerHour !== undefined ? parseFloat(ov.ratePerHour) || 0 : origRate;
      const otMult = ov.otMult !== undefined ? Math.max(1, parseFloat(ov.otMult) || 1) : globalOtMult;
      // ไม่มี override ให้ใช้ยอดที่คำนวณมาจาก MergedPayrollTableV2 ตรงๆ — ห้ามแตกเป็น hours/rate แล้วคูณกลับ
      // เพราะการปัดเศษแยกสองตัว (origHours, origRate) แล้วคูณ+ปัดอีกรอบ ทำให้ยอดคลาดจากต้นฉบับทีละเศษสตางค์
      // พอมีพนักงาน/checkin เยอะ ความคลาดนี้จะสะสมจนยอดรวมสองตารางไม่ตรงกัน (ห่างได้หลักบาท)
      const hasHoursRateOverride = ov.hours !== undefined || ov.ratePerHour !== undefined;
      // OT ขึ้นกับ ratePerHour ด้วย (ไม่ใช่แค่ otMult) — ถ้า rate ถูกแก้ ต้องคำนวณ OT ใหม่ด้วยเช่นกัน
      const hasOtRecompute = ov.ratePerHour !== undefined || otMult !== DEFAULT_OT_MULT;
      return {
        ...emp,
        hours,
        ratePerHour,
        otMult,
        origOtHours,
        inputHours: ov.hours !== undefined ? ov.hours : origHours,
        inputRate: ov.ratePerHour !== undefined ? ov.ratePerHour : origRate,
        inputOtMult: ov.otMult !== undefined ? ov.otMult : globalOtMult,
        amount: hasHoursRateOverride
          ? Math.round(hours * ratePerHour * 100) / 100
          : (emp.amount || 0),
        ot_amount: hasOtRecompute
          ? Math.round(origOtHours * ratePerHour * otMult * 100) / 100
          : (emp.ot_amount || 0),
      };
    },
    [overrides, globalOtMult],
  );

  const setOv = (projId, empId, field, value) => {
    const key = ovKey(projId, empId);
    setOverrides((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  };

  const toggleProject = (projId) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projId)) next.delete(projId);
      else next.add(projId);
      return next;
    });
  };

  const rawDashboard = React.useMemo(() => {
    const raw = timestamp?.dashboard || [];
    if (!filteredEmployeeIds || filteredEmployeeIds.size === 0) return raw;
    return raw.map((project) => {
      const emps = (project.employees || []).filter((e) => filteredEmployeeIds.has(e.employee_id?.toString()));
      if (emps.length === 0) return null;
      const total_salary = emps.reduce((s, e) => s + (e.amount || 0), 0);
      const total_ot = emps.reduce((s, e) => s + (e.ot_amount || 0), 0);
      const total_allowance = emps.reduce((s, e) => s + (e.allowance || 0), 0);
      const total_field_allowance = emps.reduce((s, e) => s + (e.field_allowance_amount || 0), 0);
      return { ...project, employees: emps, total_salary, total_ot, total_allowance, total_field_allowance, total_spent: total_salary + total_ot };
    }).filter(Boolean);
  }, [timestamp?.dashboard, filteredEmployeeIds]);

  const costResult = React.useMemo(() => {
    const base = rawDashboard.map((project) => {
      const projId = project.project_id || project.project_number;
      const employees = (project.employees || []).map((emp) => computeEmp(emp, projId));
      const total_salary = employees.reduce((s, e) => s + (e.amount || 0), 0);
      const total_ot = employees.reduce((s, e) => s + (e.ot_amount || 0), 0);
      const total_allowance = employees.reduce((s, e) => s + (e.allowance || 0), 0);
      const total_field_allowance = employees.reduce((s, e) => s + (e.field_allowance_amount || 0), 0);
      return { ...project, employees, total_salary, total_ot, total_allowance, total_field_allowance };
    });
    // คำนวณ "รวม" ด้วยสูตรกลาง (ใช้ร่วมกับ PDF export ด้วย กันสูตรเพี้ยนคนละที่) — total_sso คำนวณจาก
    // employee_deductions ของแต่ละแถวเอง (ไม่ใช่กระจาย totalSsoAmount แบบ top-down อีกต่อไป ดู projectCostFormula.js)
    // total_payable ("ที่ต้องจ่าย") มากับ base อยู่แล้ว (คำนวณที่ MergedPayrollTableV2.js — projectDashboard)
    return computeProjectCostRows(base);
  }, [rawDashboard, computeEmp]);

  // แยกโครงการที่มีพนักงาน "ภายนอก" เป็น 2 แถว (ปกติ / ภายนอก) ผลรวมทุกคอลัมน์เท่าเดิม
  const dashboard = React.useMemo(() => splitExternalRows(costResult.rows), [costResult.rows]);

  // modal รายงานต่อโครงการ (กดแถวโครงการ) — เก็บแค่ key แล้วหาแถวสดจาก dashboard ทุก render
  // ให้ตัวเลขใน modal ขยับตามตาราง (เช่นเปลี่ยนตัวคูณ OT) ขณะเปิดค้างไว้
  const [reportKey, setReportKey] = React.useState(null);
  const rowKey = (r) => `${String(r.project_id || r.project_number || 'no_project')}${r.external ? '#ext' : ''}`;

  const showStatus = Array.isArray(laborStatuses);
  const colCount = 12 + (showStatus ? 1 : 0);
  // สถานะรายการเบิกของแถวนี้: แถวปกติ = รายการปกติของโครงการ (employee_id ว่าง), แถวภายนอก = รายการรายคนของพนักงานในแถว
  const statusEntriesOf = (row) => {
    if (!showStatus) return [];
    const pid = String(row.project_id || '');
    const list = laborStatuses.filter((s) => String(s.project_id) === pid);
    if (!row.external) return list.filter((s) => !s.employee_id);
    const ids = new Set((row.employee_payables || []).map((e) => String(e.employee_id)));
    return list.filter((s) => s.employee_id && ids.has(String(s.employee_id)));
  };
  const reportRow = reportKey ? dashboard.find((r) => rowKey(r) === reportKey) : null;
  // ชื่อพนักงานจากทุกโครงการ — กันกรณีพนักงานมีแค่ยอดหัก/ที่ต้องจ่ายในโครงการนี้ (ไม่มี entry ค่าแรงให้ดึงชื่อ)
  const nameMap = React.useMemo(() => {
    const m = {};
    dashboard.forEach((r) => (r.employees || []).forEach((e) => {
      if (e.name) m[String(e.employee_id)] = e.name;
    }));
    return m;
  }, [dashboard]);

  const grandTotalSalary = React.useMemo(
    () => dashboard.reduce((s, r) => s + (r.total_salary || 0), 0),
    [dashboard],
  );

  const hasOverrides = Object.keys(overrides).length > 0 || globalOtMult !== DEFAULT_OT_MULT;
  const rec = !filteredEmployeeIds && !hasOverrides ? timestamp?.reconciliation : null;

  const renderReconciliation = () => {
    if (!rec) return null;
    const { balanced } = rec;
    const bgColor = balanced ? '#f0fdf4' : '#fff7ed';
    const borderColor = balanced ? '#16a34a' : '#f97316';
    const textColor = balanced ? '#15803d' : '#c2410c';
    const Icon = balanced ? CheckCircleIcon : WarningAmberIcon;
    const salaryTooltip = rec.salary_balanced
      ? `ค่าแรงตรง: ${fmtCur(rec.employee_total_salary)}`
      : `ค่าแรงพนักงาน: ${fmtCur(rec.employee_total_salary)} | ตามโปรเจค: ${fmtCur(rec.project_total_salary)} | ส่วนต่าง: ${fmtCur(rec.diff_salary)}`;
    const otTooltip = rec.ot_balanced
      ? `OT ตรง: ${fmtCur(rec.employee_total_ot)}`
      : `OT พนักงาน: ${fmtCur(rec.employee_total_ot)} | ตามโปรเจค: ${fmtCur(rec.project_total_ot)} | ส่วนต่าง: ${fmtCur(rec.diff_ot)}`;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 6, marginTop: 8, fontSize: '0.82rem', color: textColor, fontWeight: 500, flexWrap: 'wrap' }}>
        <Icon style={{ fontSize: 18, color: borderColor }} />
        <span>{balanced ? 'ยอดรวมตรงกัน' : 'ยอดรวมไม่ตรง'}</span>
        <span style={{ margin: '0 4px', opacity: 0.4 }}>|</span>
        <span title={salaryTooltip} style={{ cursor: 'default', textDecoration: rec.salary_balanced ? 'none' : 'underline dotted' }}>
          ค่าแรง {rec.salary_balanced ? '✓' : `ต่าง ${fmtCur(Math.abs(rec.diff_salary))} บาท`}
        </span>
        <span style={{ margin: '0 4px', opacity: 0.4 }}>|</span>
        <span title={otTooltip} style={{ cursor: 'default', textDecoration: rec.ot_balanced ? 'none' : 'underline dotted' }}>
          OT {rec.ot_balanced ? '✓' : `ต่าง ${fmtCur(Math.abs(rec.diff_ot))} บาท`}
        </span>
      </div>
    );
  };

  const renderEmployeeSubTable = (row) => {
    const projId = row.project_id || row.project_number;
    const emps = row.employees || [];
    if (emps.length === 0) return null;
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', background: '#fffbf5', borderTop: '1px dashed #fed7aa' }}>
        <thead>
          <tr>
            {['ชื่อพนักงาน', 'ชม.', '฿/ชม', 'ชม.OT', 'ตัวคูณ OT', 'ค่าแรง', 'OT'].map((h) => (
              <th key={h} style={{ ...TH, fontSize: '0.73rem', color: '#7c2d12', padding: '4px 6px' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {emps.map((emp, i) => (
            <tr key={emp.employee_id || i}>
              <td style={{ ...TD_ROW, padding: '4px 6px' }}>
                {emp.name || emp.employee_id}
                {emp.role && <span style={{ color: '#9ca3af', marginLeft: 4, fontSize: '0.7rem' }}>{`(${emp.role})`}</span>}
              </td>
              <td style={{ ...TD_ROW, padding: '4px 4px', width: 80 }}>
                <input type="number" value={emp.inputHours} min={0} step={0.5} style={{ ...IS, width: 72 }} onChange={(e) => setOv(projId, emp.employee_id, 'hours', e.target.value)} />
              </td>
              <td style={{ ...TD_ROW, padding: '4px 4px', width: 110 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input type="number" value={emp.inputRate} min={0} step={1} style={{ ...IS, width: 80 }} onChange={(e) => setOv(projId, emp.employee_id, 'ratePerHour', e.target.value)} />
                  <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>฿</span>
                </div>
              </td>
              <td style={{ ...TD_ROW, textAlign: 'center', color: '#6b7280', width: 60 }}>{emp.origOtHours > 0 ? emp.origOtHours : '—'}</td>
              <td style={{ ...TD_ROW, padding: '4px 4px', width: 80 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input type="number" value={emp.inputOtMult} min={1} max={5} step={0.25} style={{ ...IS, width: 56 }} onChange={(e) => setOv(projId, emp.employee_id, 'otMult', e.target.value)} />
                  <span style={{ fontSize: '0.7rem', color: '#6b7280' }}>×</span>
                </div>
              </td>
              <td style={{ ...TD_ROW, textAlign: 'right', fontWeight: 500 }}>{fmtCur(emp.amount)}</td>
              <td style={{ ...TD_ROW, textAlign: 'right', fontWeight: 500 }}>{fmtCur(emp.ot_amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  void toggleProject;
  void renderEmployeeSubTable;

  return (
    <div style={{ padding: '8px 0' }}>
      <div style={{ borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.12)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>
        <div style={{ padding: 16 }}>
          {/* ตัวคูณ OT toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: 500 }}>ตัวคูณ OT:</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid #d1d5db', borderRadius: 4, overflow: 'hidden', height: 32 }}>
              <input type="number" value={globalOtMult} min={1} max={5} step={0.25}
                style={{ width: 44, padding: '0 6px', border: 'none', outline: 'none', fontSize: '0.875rem', textAlign: 'right' }}
                onChange={(e) => setGlobalOtMult(Math.max(1, parseFloat(e.target.value) || 1))} />
              <span style={{ padding: '0 6px', color: '#6b7280', fontSize: '0.8rem', borderLeft: '1px solid #e5e7eb', height: '100%', display: 'flex', alignItems: 'center' }}>×</span>
            </div>
            {[1.0, 1.5, 2.0, 3.0].map((v) => (
              <button key={v} onClick={() => setGlobalOtMult(v)}
                style={{ padding: '2px 10px', borderRadius: 12, border: `1px solid ${globalOtMult === v ? '#f97316' : '#d1d5db'}`, background: globalOtMult === v ? '#fff7ed' : '#fff', color: globalOtMult === v ? '#f97316' : '#374151', cursor: 'pointer', fontSize: '0.8rem', fontWeight: globalOtMult === v ? 700 : 400 }}>
                ×{v}
              </button>
            ))}
            {hasOverrides && (
              <button title="รีเซ็ตค่าทั้งหมดกลับต้นฉบับ" onClick={() => { setOverrides({}); setGlobalOtMult(DEFAULT_OT_MULT); }}
                style={{ padding: '3px 6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                <RestartAltIcon style={{ fontSize: 18 }} />
              </button>
            )}
            {hasOverrides && <span style={{ fontSize: '0.72rem', color: '#f97316', fontWeight: 500 }}>* กำลังใช้ค่าที่ปรับแต่ง</span>}
            {onOpenFormulaExplain && (
              <button
                onClick={onOpenFormulaExplain}
                title="ดูวิธีคำนวณค่าแรงของทุกคนในงวดนี้ และการกระจายลงแต่ละโครงการ"
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, padding: '4px 12px', borderRadius: 6, border: '1px solid #f97316', background: '#fff7ed', color: '#c2410c', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
              >
                <CalculateOutlinedIcon style={{ fontSize: 16 }} />
                ดูสูตรคำนวณค่าแรง
              </button>
            )}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th colSpan={colCount} style={{ background: '#f97316', color: '#fff', fontWeight: 600, padding: '10px 8px', textAlign: 'center', fontSize: '0.95rem' }}>
                    ค่าแรงพนักงาน (ไม่หักภาษีและประกันสังคม)
                  </th>
                </tr>
                <tr>
                  {['โครงการ', 'ค่าแรง', 'OT', 'ปกส.', 'หักสาย', 'หัก CRL', 'หักอื่นๆ', 'เบี้ยเลี้ยง', 'ค่าออกสนาม', 'เงินได้อื่นๆ', 'ที่ต้องจ่าย', ...(showStatus ? ['สถานะรายการเบิก'] : []), 'ต้นทุน'].map((h) => (
                    <th key={h} style={TH}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dashboard?.length > 0 ? (
                  <>
                    {dashboard.map((row, index) => (
                      <tr key={row.id || index} onMouseEnter={(e) => { e.currentTarget.style.background = '#fff7ed'; }} onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
                        <td style={{ ...TD_ROW, whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Tooltip title="รายงานค่าแรงตามโครงการ — ดูว่าแต่ละยอดมาจากพนักงานคนไหน">
                              <IconButton
                                size="small"
                                onClick={() => setReportKey(rowKey(row))}
                                aria-label="รายงานค่าแรงตามโครงการ"
                                sx={{ color: '#ea580c', p: 0.5 }}
                              >
                                <AssessmentOutlinedIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                            {onOpenProjectFormula && (
                              <Tooltip title="สูตรคำนวณของโครงการนี้ — ยอดมาจากใคร คนละกี่บาท">
                                <IconButton
                                  size="small"
                                  onClick={() => onOpenProjectFormula(row)}
                                  aria-label="สูตรคำนวณของโครงการ"
                                  sx={{ color: '#c2410c', p: 0.5 }}
                                >
                                  <CalculateOutlinedIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <span>{row?.project_number || 'ไม่ระบุโครงการ'}</span>
                            {row?.external && (
                              <Tooltip title={`พนักงานที่ไม่จ่ายเงินตามโครงการ: ${(row.employees || []).map((e) => e.name).filter((n, i, a) => n && a.indexOf(n) === i).join(', ') || '-'}`}>
                                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#7c2d12', background: '#fed7aa', borderRadius: 10, padding: '1px 8px' }}>
                                  ภายนอก
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>
                          {fmtCur(row?.total_salary)}
                          {grandTotalSalary > 0 && (
                            <div style={{ fontSize: '0.68rem', color: '#c2410c', fontWeight: 500 }}>
                              {((row?.total_salary || 0) / grandTotalSalary * 100).toFixed(1)}%
                            </div>
                          )}
                        </td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_ot)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_sso)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_late)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right', color: (row?.total_crl || 0) > 0 ? '#b91c1c' : undefined }}>{fmtCur(row?.total_crl)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_other)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_allowance)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_field_allowance)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_bonus)}</td>
                        <td style={{ ...TD_ROW, textAlign: 'right', fontWeight: 600, color: '#c2410c' }}>
                          {fmtCur(row?.total_payable)}
                        </td>
                        {showStatus && (
                          <td style={{ ...TD_ROW, textAlign: 'center' }}>
                            {(() => {
                              const entries = statusEntriesOf(row);
                              if (entries.length === 0) {
                                return <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>ยังไม่สร้าง</span>;
                              }
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                                  {entries.map((s) => {
                                    const info = laborStatusInfo(s.status);
                                    const empName = s.employee_id
                                      ? (row.employees || []).find((e) => String(e.employee_id) === String(s.employee_id))?.name
                                      : null;
                                    return (
                                      <Tooltip key={s.id || `${s.project_id}-${s.employee_id || 'main'}`} title={`${s.code || ''}${empName ? ` · ${empName}` : ''}`.trim() || info.label}>
                                        <span style={{ fontSize: '0.7rem', fontWeight: 700, background: info.bg, color: info.color, borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' }}>
                                          {entries.length > 1 && empName ? `${empName}: ` : ''}{info.label}
                                        </span>
                                      </Tooltip>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                          </td>
                        )}
                        <td style={{ ...TD_ROW, textAlign: 'right' }}>{fmtCur(row?.total_spent)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td style={{ ...TD_TOTAL, textAlign: 'center' }}>รวมทั้งหมด</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_salary || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_ot || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_sso || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_late || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_crl || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_other || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_allowance || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_field_allowance || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_bonus || 0), 0))}</td>
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_payable || 0), 0))}</td>
                      {showStatus && <td style={TD_TOTAL} />}
                      <td style={TD_TOTAL}>{fmtCur(dashboard.reduce((s, r) => s + (r.total_spent || 0), 0))}</td>
                    </tr>
                  </>
                ) : (
                  <tr>
                    <td colSpan={colCount} style={{ textAlign: 'center', padding: '40px 8px', color: '#9ca3af', fontSize: '0.95rem' }}>
                      ไม่มีข้อมูล
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {renderReconciliation()}
        </div>
      </div>
      <ProjectCostReportDialog row={reportRow} nameMap={nameMap} onClose={() => setReportKey(null)} />
    </div>
  );
}
